/**
 * Working through a source's fields after it exists.
 *
 * Three things the Add Source flow cannot do, because each only makes sense
 * once there are Feeds and saved Bindings to compare against:
 *
 * - **The review queue.** Unknown paths were allowed to default without
 *   blocking the save. This is where they get worked through — later, or never,
 *   but not at the cost of getting the source running.
 * - **Which Feed a field came from.** A source is not always one endpoint. PFF
 *   publishes thirteen reports, most of which carry a column called `yards`
 *   meaning something different in each: one quarterback has 3,951 in the
 *   passing report and 143 in the rushing one. A screen keying a field on its
 *   path alone shows those as one row and edits both at once, which is worse
 *   than showing neither.
 * - **Preview.** A sample of parsed player rows reflecting the Bindings *on
 *   screen* rather than the ones in the database, so a change can be judged
 *   before it is committed to. Scope is the change most worth judging: adopting
 *   one renames a published column.
 *
 * Re-reading the source preserves everything already classified and marks only
 * what is new, so a publisher adding a field does not mean starting over.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  api,
  type Binding,
  type BindingDraft,
  type DiscoveredField,
  type DiscoveryResult,
  type Role,
  type SourceProfile,
  type StatEntry,
} from "../api";
import { Layout } from "../components/Layout";
import {
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  Code,
  Muted,
  PillGroup,
  SelectCompact,
  Spinner,
  TableScroll,
} from "../components/ui";
import { EyeIcon, RefreshIcon } from "../components/icons";
import {
  HintedHeading,
  NewStatisticForm,
  NewStatisticRow,
  ORDER_HINT,
  OrderCell,
  OrderNudge,
  ProblemNote,
  ROLE_OPTIONS,
  SampleCell,
  StatisticCell,
  WeightCell,
} from "../components/FieldMapping";
import {
  bindingProblems,
  firstMarkedRow,
  markedNotice,
  markedRows,
  problemsFromRefusal,
  rowSignature,
  type ProblemRow,
} from "../binding-problems";
import {
  childRows,
  collapseField,
  dropSupersededUnscoped,
  evidenceByPath,
  evidenceFor,
  expansionHazard,
  groupByField,
  isDuplicated,
  isPerReport,
  matchReportsFor,
  materialiseReports,
  reportsKnown,
  reportsOf,
  triageCounts,
  unreadNotice,
  withoutReports,
  type ExpansionHazard,
  type FieldEvidence,
  type FieldGroup,
} from "../field-reports";
import { weightNotice } from "../stat-weight";
import {
  moveRow,
  nudgeLimits,
  orderNotice,
  renumber,
  type OrderedField,
} from "../column-order";
import { labelsFor } from "../position-exclusions";
import { awaitDiscoveryJob } from "../discovery-job";
import { withRetry } from "../retry";
import { keys } from "../queries";

interface FieldRow {
  path: string;
  role: Role;
  statKey: string | null;
  reviewed: boolean;
  isNew: boolean;
  samples: unknown[];
  occurrences: number | null;
  /** The Feed this Binding reads, or null to read every Feed. Where more than
   *  one of them answers, each gets its own published column (ADR-0055). */
  feedId: string | null;
  /**
   * What this statistic is worth in the Impact Factor (ADR-0040), or null for
   * nothing declared — which weighs 1.
   */
  weight: number | null;
  /**
   * Where this field's column sits in the published header (ADR-0058), or null
   * for nothing declared — which sorts after everything declared.
   *
   * Numbered within its block, so a `carried` field at 1 is the first carried
   * column rather than the first column in the file. `src/column-order.ts`
   * holds the rule and mirrors the server's.
   */
  sortOrder: number | null;
  /**
   * How to read the value: `{}` for an ordinary scalar, and a composed rule for
   * a publisher whose spelling needs one — `{type: "duration"}` reads MLS NEXT
   * Pro's `HH:MM:SS` playing time as minutes, `{type: "join", with: [...]}`
   * builds a name out of the two fields it splits one across.
   *
   * **Carried rather than rebuilt.** This screen used to emit `parse_rule: {}`
   * for every row, so opening a composed profile's Fields and pressing Save
   * dropped the rules the template had declared — and the next Run published a
   * column of unread strings and a file of forenames, with nothing on the
   * screen saying anything had changed.
   */
  parseRule: Record<string, unknown>;
  /**
   * Which Feeds were found publishing this path, from the last read.
   *
   * Empty until the source has been re-read, because a saved Binding says
   * nothing about which reports carry its path. Splitting a field across Feeds
   * that were never read would declare Bindings against reports that do not
   * publish it, so the control is offered only once there is evidence.
   */
  seenIn: string[];
  /**
   * What each Feed published for this path, keyed by Feed.
   *
   * `samples` above is one report's — whichever discovery listed first — and
   * that was all the screen had while a duplicated field was one row. Deciding
   * whether two reports mean one statistic or two is a decision about their
   * *values*, so the per-report expansion needs them side by side: 3,951
   * passing yards against 143 rushing is the evidence, and the field's name is
   * not.
   *
   * Costs no endpoint. Discovery already returns one `DiscoveredField` per
   * `(feed, path)` carrying its own samples; the merge below was throwing all
   * but one of them away.
   */
  samplesByFeed: Record<string, unknown[]>;
}

/**
 * A row's identity is its Feed and its path, never its path alone.
 *
 * `yards` in the passing report and `yards` in the rushing report are two
 * Bindings meaning two different things, so keying by path would render them as
 * one row and edit both at once.
 */
function rowKey(row: { feedId: string | null; path: string }): string {
  return `${row.feedId ?? ""}::${row.path}`;
}

/**
 * The rows as `column-order` reads them: one row, one Binding, one position.
 *
 * In one place because four things ask the same question — the notice, the
 * nudge limits, Renumber and a nudge itself — and four inline projections that
 * agree today are four that can stop agreeing.
 *
 * `feedPosition` is the Feed's index in the profile's own list, which is the
 * server's own `position`: `SourceProfile.feeds` is mapped `order_by
 * "Feed.position"`, so the list arrives sorted and the index is the number.
 * Zero for a row that reads every Feed, which has no single position and whose
 * columns the server orders by the report that published them.
 */
function orderedFieldsFrom(
  rows: readonly FieldRow[],
  profile: SourceProfile | null | undefined,
): OrderedField[] {
  const feedPositions = new Map((profile?.feeds ?? []).map((feed, index) => [feed.id, index]));
  return rows.map((row) => ({
    key: rowKey(row),
    path: row.path,
    role: row.role,
    statKey: row.statKey,
    sortOrder: row.sortOrder,
    feedPosition: row.feedId === null ? 0 : (feedPositions.get(row.feedId) ?? 0),
  }));
}

export function ReviewFields() {
  const { profileId = "" } = useParams();

  const queryClient = useQueryClient();
  /**
   * Which slice of the review is on screen.
   *
   * Two of these four are about the per-report work, and `Duplicated` is a
   * measurement the client has never been shown: on the FBS profile 67 of the
   * 248 distinct metric names are published by more than one report, which is
   * what makes a section-per-report page 379 rows long.
   *
   * **How many of those 67 need a decision is a different number and is not
   * shown**, because this screen cannot compute it — see `TriageCounts`. The
   * nearest published figure is ADR-0055's, and it is 7 of **22** shared
   * columns measured on captured runs, not 7 of these 67 metric names. The two
   * are different denominators from different methods and are not restated as
   * one here.
   */
  const [queue, setQueue] = useState<"all" | "unreviewed" | "duplicated" | "perReport">("all");
  /**
   * Which fields are showing their reports.
   *
   * Collapsed is the default and the page does not grow at all — it grows only
   * by the field being worked on, and shrinks back. A field already given a
   * statistic per report opens on its own, because its configuration is in the
   * children and a closed row would hide the only place it can be read.
   */
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  /** Which row's "+ New" form is open, keyed the way every row is: by Feed and
   *  path, never by path alone. */
  const [newStatFor, setNewStatFor] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // How long the current re-read has been going, in milliseconds. The read is
  // polled rather than awaited, and this is the only thing on screen that
  // distinguishes "still reading" from "stuck" — which matters here more than
  // anywhere, because a thirteen-report source legitimately takes minutes and
  // a reviewer with no sign of progress presses the button again.
  const [reReadElapsedMs, setReReadElapsedMs] = useState(0);
  /** Flipped when this screen goes away; abandons the poll, never the read.
   *  The server finishes it, and the next press joins it rather than
   *  re-reading every Feed on the profile. */
  const gone = useRef({ aborted: false });
  useEffect(() => {
    // **Cleared on mount as well as set on unmount**, which is not symmetry
    // for its own sake: `main.tsx` renders under `React.StrictMode`, so in
    // development every component is mounted, unmounted and mounted again
    // while the *same* ref object survives. Setting the flag only in the
    // cleanup would leave it true for the real mount that follows, and every
    // scan would abandon itself on its first poll — in development only,
    // which is the worst place for it to hide.
    gone.current.aborted = false;
    return () => {
      gone.current.aborted = true;
    };
  }, []);

  const [profileQuery, bindingsQuery, statsQuery, reportsQuery] = useQueries({
    queries: [
      { queryKey: keys.profile(profileId), queryFn: () => api.readProfile(profileId) },
      { queryKey: keys.bindings(profileId), queryFn: () => api.listBindings(profileId) },
      { queryKey: keys.stats, queryFn: () => api.listStats() },
      /**
       * What each Feed was last seen publishing (ADR-0061).
       *
       * **The query that makes the per-report disclosure work on load**, which
       * is the whole of `field-mapping-usability/03`. It reaches no publisher:
       * a stored reading, fetched in parallel with the three above and costing
       * one more row read, where re-deriving it means the serial re-read of
       * every Feed — measured at 23.7s over NCAA Division II's seventeen
       * leaderboards on 2026-11-02 (`field-mapping-usability/03`, *Verified*),
       * and around three minutes on PFF's thirteen reports, which answer far
       * more slowly.
       */
      {
        queryKey: keys.publishedFields(profileId),
        queryFn: () => api.listPublishedFields(profileId),
      },
    ],
  });

  const profile = profileQuery.data ?? null;
  const stats = statsQuery.data ?? [];

  /**
   * The statistics this profile's sport actually has.
   *
   * The dropdown offered all of them until now, which the suggestion engine has
   * always refused to do and says why in `_index_dictionary` (ADR-0017): a
   * soccer field called `penalties` bound to football's entry inherits
   * `lower_is_better` and ranks every team backwards, silently. Blocking that
   * automatically and leaving it a click away by hand is not a filter.
   *
   * Filtered here rather than by asking the API for one sport, which is what
   * `AddSource` does. Two reasons, both particular to this screen. The sport is
   * a property of the profile, so a scoped request could not be issued until
   * `profileQuery` resolved — turning three parallel queries into two round
   * trips on the screen that already waits for a discovery. And
   * `NewStatisticForm` checks a new name against the whole dictionary, so the
   * unfiltered list is loaded here regardless; scoping the request would mean
   * fetching it twice to get both answers.
   */
  const sportEntries = useMemo(
    () => (profile ? stats.filter((entry) => entry.sport === profile.sport) : []),
    [stats, profile],
  );

  /**
   * What feeds each of this sport's Canonical Position Groups, for the detail
   * panel under the Statistic menu.
   *
   * Fetched once here and handed to every cell rather than queried per row:
   * one cache key either way, but a table of 488 rows would otherwise hold 488
   * subscriptions to it. Shared with the Stat Dictionary screen by key, so a
   * review that opens both asks the server once.
   */
  const { data: positionGroups } = useQuery({
    queryKey: keys.positionGroups,
    queryFn: api.listPositionGroups,
  });
  const groupLabels = useMemo(
    () => labelsFor(positionGroups, profile?.sport ?? ""),
    [positionGroups, profile?.sport],
  );

  /**
   * A stored `stat_key` this profile's sport has no entry for, resolved
   * wherever it does exist so the cell can name it.
   *
   * Keys are unique per sport and not globally (ADR-0017), so a key filtered
   * out of `sportEntries` may still be several sports' — `penalties` is both
   * football's and soccer's. Which one a Binding meant is not recorded and
   * cannot be recovered: `stat_key` is a bare string. The first match names the
   * option, and the option says which sport it came from, so a guess is
   * presented as the guess it is rather than as this profile's statistic.
   */
  const outOfSportFor = useMemo(() => {
    const byKey = new Map<string, StatEntry>();
    for (const entry of stats) {
      if (entry.sport !== profile?.sport && !byKey.has(entry.key)) byKey.set(entry.key, entry);
    }
    return (statKey: string | null) => (statKey === null ? null : byKey.get(statKey) ?? null);
  }, [stats, profile]);

  /** What a Feed is called on screen: its name, or its address as a last resort. */
  const feedName = useMemo(() => {
    const names = new Map<string, string>();
    for (const feed of profile?.feeds ?? []) names.set(feed.id, feed.label || feed.url);
    return (id: string) => names.get(id) ?? "a Feed that has gone";
  }, [profile]);

  /**
   * The editable copy of the Bindings.
   *
   * This screen is a form over server data, so the rows have to survive being
   * edited: they are seeded from the query and then owned locally until a save
   * lands. `rowsFor` records which fetch a draft was seeded from, so a save —
   * which invalidates the query — reseeds, while an ordinary re-render does not
   * throw away what has been typed.
   */
  const [rows, setRows] = useState<FieldRow[]>([]);
  const [rowsFor, setRowsFor] = useState<unknown>(null);

  /**
   * Which reports publish each path, from the stored reading (ADR-0061).
   *
   * Pivoted out of the per-Feed shape the endpoint returns, which is the shape
   * the fact is stored in. Empty for a profile whose Feeds nothing has read
   * since ADR-0061 shipped — the one state that still asks for a re-read, and
   * the only one left.
   */
  const evidence = useMemo(
    () =>
      evidenceByPath(
        (reportsQuery.data?.feeds ?? []).map((feed) => ({
          feedId: feed.feed_id,
          fields: feed.fields,
        })),
      ),
    [reportsQuery.data],
  );

  /**
   * Whether **every** one of this profile's Feeds carries a reading (ADR-0061).
   *
   * What tells *no Feed publishes this path* from *nobody has looked*. Both are
   * nought reports on a field, and only the second is worth the serial re-read
   * — so a Binding on a path the publisher has dropped says so, instead of
   * sending the reviewer off to confirm a nought.
   *
   * Read off `read_at` rather than off a non-empty `fields`, because a Feed
   * that genuinely publishes nothing is an empty reading somebody did take.
   * `reportsKnown` owns the `every`-not-`some` rule and the reason for it.
   */
  const feedsRead = useMemo(
    () =>
      reportsKnown((reportsQuery.data?.feeds ?? []).map((feed) => ({ readAt: feed.read_at }))),
    [reportsQuery.data],
  );

  /**
   * Seeded once both reads have landed, never from the Bindings alone.
   *
   * `rowsFor` tracks the Bindings fetch, so a draft survives re-renders and is
   * replaced only by a save. The evidence has to be in hand when the rows are
   * built, because `fromBinding` is where `seenIn` is filled and rebuilding
   * every row later would discard whatever had been typed in the meantime —
   * which is why this waits for `reportsQuery` rather than seeding twice.
   *
   * It waits on the fetch *settling* rather than succeeding: a profile whose
   * Feeds have never been read legitimately answers with nothing, and a screen
   * that only ever rendered on a non-empty reading would hang on exactly the
   * profiles this feature cannot help.
   */
  useEffect(() => {
    if (bindingsQuery.data && !reportsQuery.isPending && bindingsQuery.data !== rowsFor) {
      setRows(bindingsQuery.data.map((binding) => fromBinding(binding, evidence)));
      setRowsFor(bindingsQuery.data);
    }
  }, [bindingsQuery.data, reportsQuery.isPending, evidence, rowsFor]);

  const reRead = useMutation({
    // Every Feed, not the first one. A thirteen-report source read from report
    // one shows fifty fields of six hundred, and none of them attributed to the
    // report that published them.
    mutationFn: () => {
      if (!profile?.feeds.length) {
        throw new ApiError(0, "no_feed", "This source has no Feed to read.");
      }
      setReReadElapsedMs(0);
      // **Started and polled, not awaited in one request.** This is the
      // slowest call the portal makes: every Feed on the profile behind a
      // single read, which for PFF's FCS template is thirteen addresses, one
      // of them measured at about two minutes on its own. A Netlify proxy
      // rewrite gives any one request twenty-six seconds, so holding this open
      // meant the edge answered the browser while the read was still going.
      // Wrapped in `withRetry` the way Add Source's scan is, and it matters
      // more here: this is one job covering every Feed on the profile, so a
      // failure discards a read of all thirteen rather than one of them. The
      // retry is cheap because the server joins a resubmission to the read
      // already running — it re-attaches instead of re-reading.
      return withRetry(
        () =>
          awaitDiscoveryJob<DiscoveryResult>(
            () => api.startDiscoveryJob({ source_profile_id: profile.id }),
            (jobId) => api.readDiscoveryJob(jobId),
            {
              fail: (status, code, message, field, details) =>
                new ApiError(status, code, message, field, details),
              signal: gone.current,
              onPoll: setReReadElapsedMs,
            },
          ),
      );
    },
    onSuccess: (result) => {
      // Normalised after the merge rather than before it, because the evidence
      // the normalisation reads is what the merge has just written: `seenIn` is
      // filled by a read and by nothing else, so a field whose reports are all
      // scoped can only be recognised as such once this read has named them.
      setRows((current) => dropSupersededUnscoped(merge(current, result.fields)));
      // The re-read **wrote** what each Feed publishes (ADR-0061), so the
      // stored reading this screen loaded with is now the old one. Dropped
      // rather than left: `merge` has already taken the fresh reports onto the
      // rows, but `feedsRead` is read off `read_at`, and without this a profile
      // that had never been read keeps answering "Re-read to see Feeds" for the
      // rest of the session — on a field the re-read has just proved no Feed
      // publishes, which is the one message ADR-0061 says cannot help.
      void queryClient.invalidateQueries({ queryKey: keys.publishedFields(profileId) });
      const feeds = result.feeds.length === 1 ? "1 Feed" : `${result.feeds.length} Feeds`;
      const one = result.new_field_count === 1;
      setNotice(
        result.new_field_count === 0
          ? `Nothing new. Every field across ${feeds} is already classified.`
          : `${result.new_field_count} field${one ? " is" : "s are"} new across ${feeds} since this profile was saved.`,
      );
    },
  });

  const drafts = useMemo(() => rows.map(toDraft), [rows]);

  /**
   * The review as the save's own rules read it.
   *
   * Keyed by Feed and path, exactly as the table is, because that is what a
   * problem has to be put against: `yards` in the passing report and `yards`
   * in the rushing one are two rows, and marking both for one report's
   * collision would mark a row that is fine.
   */
  const reviewRows = useMemo<ProblemRow[]>(
    () =>
      rows.map((row) => ({
        key: rowKey(row),
        path: row.path,
        role: row.role,
        statKey: row.statKey,
        feedId: row.feedId,
        parseRule: row.parseRule,
      })),
    [rows],
  );

  /**
   * What this review would be refused for, while it is being made.
   *
   * The same rules as `app/domain/binding_problems.py`, against the rows on
   * screen — so the collision that used to surface as a sentence beside the
   * Save button is marked on the second of the two rows that cause it, as the
   * statistic is chosen. The server stays the authority: this knows only the
   * dictionary this tab was handed, and `sportEntries` is already scoped to the
   * profile's sport (ADR-0017).
   */
  const liveProblems = useMemo(
    () =>
      profile
        ? bindingProblems(reviewRows, {
            knownStatKeys: sportEntries.map((entry) => entry.key),
            feedIds: profile.feeds.map((feed) => feed.id),
            sport: profile.sport,
          })
        : [],
    [reviewRows, sportEntries, profile],
  );

  const save = useMutation({
    mutationFn: () => api.replaceBindings(profileId, drafts),
    onSuccess: async () => {
      setNotice("Bindings saved.");
      // The saved Bindings change the profile's unreviewed count too, which the
      // dashboard and the source screen both show.
      await queryClient.invalidateQueries({ queryKey: keys.profile(profileId) });
      await queryClient.invalidateQueries({ queryKey: keys.profiles });
    },
  });

  // The rows on screen, not the ones in the database: previewing what is
  // already saved cannot answer "should I save this?". Until the Bindings have
  // loaded there are no rows to stand for them, and sending none would preview
  // an unbound profile rather than this one.
  const runPreview = useMutation({
    mutationFn: () => api.preview(profileId, bindingsQuery.data ? drafts : undefined),
  });

  const preview = runPreview.data ?? null;
  const busy = reRead.isPending || save.isPending || runPreview.isPending;
  const error =
    messageFor(profileQuery.error ?? bindingsQuery.error ?? statsQuery.error, "Could not load this source.") ??
    messageFor(reRead.error, "Could not read the source.") ??
    messageFor(save.error, "Could not save the bindings.") ??
    messageFor(runPreview.error, "Could not build a preview.");

  /**
   * The rows, by key, so a problem can be scrolled to.
   *
   * A ref map rather than state: nothing renders from these, and a `setState`
   * per row as a six-hundred-row table mounts would re-render the screen six
   * hundred times.
   */
  const rowAnchors = useRef(new Map<string, HTMLElement>());

  /**
   * What each row said when the last Save was sent, so a mark taken from a
   * refusal stops being shown once its row is edited — and stays on every row
   * that has not been. See `rowSignature`.
   */
  const submittedRows = useRef(new Map<string, string>());

  /**
   * A refusal that arrived anyway, put against the rows it names.
   *
   * Two ways to meet one with the rules running locally: a Stat Dictionary
   * edited in another tab — everyone sees every source (ADR-0016), so another
   * person's correction lands mid-review — and any 422 these rules do not
   * model. The second kind has no row to blame and stays in the banner.
   */
  const serverProblems = useMemo(() => {
    // The preview is refused on exactly the same terms as the save — one
    // `_validate_bindings` serves both — so its refusal names rows in the same
    // way, and showing it only as a sentence would be the defect this change is
    // about, reintroduced beside a different button.
    const refusal =
      save.error instanceof ApiError
        ? save.error
        : runPreview.error instanceof ApiError
          ? runPreview.error
          : null;
    return refusal ? problemsFromRefusal(refusal, reviewRows, submittedRows.current) : [];
  }, [save.error, runPreview.error, reviewRows]);

  /**
   * What to say under each row, live problems first.
   *
   * Live first because they describe the rows as they *are*. A refusal
   * describes the review at the moment Save was pressed, and leaving it on a
   * row since corrected would be the screen contradicting itself.
   */
  const problemByRow = useMemo(
    () => markedRows(liveProblems, serverProblems),
    [liveProblems, serverProblems],
  );

  /**
   * Which row to travel to once the marks have been painted.
   *
   * It cannot happen in the click handler, and for a reason particular to this
   * screen: a marked row may be *hidden*. The Needs-review filter shows only
   * unclassified fields, and every row these rules complain about is
   * classified — so travelling has to clear the filter first and scroll on the
   * render that follows.
   */
  const [travelToRow, setTravelToRow] = useState<string | null>(null);

  useEffect(() => {
    if (!travelToRow) return;
    const target = rowAnchors.current.get(travelToRow);
    const calm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    target?.scrollIntoView({ behavior: calm ? "auto" : "smooth", block: "center" });
    target?.focus({ preventScroll: true });
    setTravelToRow(null);
  }, [travelToRow]);

  /**
   * A refused save brings its refusal into view, at the row that earned it.
   *
   * Keyed on `isError` rather than on the error, so pressing Save twice
   * against the same refusal travels twice.
   */
  useEffect(() => {
    if (!save.isError) return;
    const refused = firstMarkedRow(serverProblems);
    if (refused) {
      setQueue("all");
      setTravelToRow(refused);
    }
    // Read at the moment the failure lands, which is what `isError` marks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [save.isError]);

  /**
   * Pressing Save either saves or says which rows are in the way.
   *
   * The local refusal is not a convenience: `replaceBindings` **replaces** the
   * profile's whole review, so a round trip refused by the server leaves the
   * reader looking at a screen full of edits with one sentence beside the
   * button. Refusing here marks every row at fault instead, without a request.
   */
  function attemptSave() {
    const blocked = firstMarkedRow(liveProblems);
    if (blocked) {
      setNotice(null);
      setQueue("all");
      setTravelToRow(blocked);
      return;
    }
    submittedRows.current = new Map(reviewRows.map((row) => [row.key, rowSignature(row)]));
    save.mutate();
  }

  /** What the banner above the table says about the rows it has marked. */
  const marked = markedNotice(problemByRow.size);

  const outstanding = useMemo(
    () => rows.filter((row) => row.role === "ignored" && !row.reviewed).length,
    [rows],
  );
  const carriedCount = useMemo(() => rows.filter((row) => row.role === "carried").length, [rows]);

  /**
   * The one thing worth saying about the whole set of declared weights.
   *
   * **Over `rows` rather than `visible`.** The Needs-review filter hides rows
   * without unbinding them, and a total computed over what is on screen would
   * change as the filter was toggled while the Run it describes did not.
   *
   * **A row scored but with no statistic chosen is excluded.** `runner.py` keys
   * the weight map by the *emitted* stat key, so a `stat` row with no `stat_key`
   * contributes nothing to the ranker's divisor. Counting it here would judge a
   * half-finished review — the one state this screen exists to work through —
   * against rows that do not rank.
   *
   * This also produced a per-row share map, keyed by Feed and path, for the
   * line under each Weight box. That line is gone, so what remains is the
   * notice alone rather than a map nothing reads.
   */
  const weightNoticeForReview = useMemo(() => {
    const scored = rows
      .filter((row) => row.role === "stat" && row.statKey)
      .map((row) => ({ key: rowKey(row), weight: row.weight }));
    return weightNotice(scored);
  }, [rows]);

  /**
   * The rows as the ordering rule reads them.
   *
   * **Over `rows` rather than `visible`**, for the reason the weight notice is:
   * the queue pills hide rows without unbinding them, and a collision between a
   * visible row and a hidden one is still a collision in the published header.
   *
   * Built once and reused by the notice, the nudge limits and both resequencing
   * buttons, so there is one answer to "where does this row sit" rather than
   * four that agree today.
   */
  const orderRows = useMemo(() => orderedFieldsFrom(rows, profile), [rows, profile]);

  const orderNoticeForReview = useMemo(() => orderNotice(orderRows), [orderRows]);

  /** Which way each row can be nudged, from the module that decides order. */
  const nudgeDisabled = useMemo(() => nudgeLimits(orderRows), [orderRows]);

  /**
   * Write a resequenced block's positions back onto the rows.
   *
   * The positions move and the array does not: these rows are the order the
   * table was seeded in, and reshuffling them would move every row under the
   * cursor of whoever pressed the button. The header follows the stored number,
   * which is the whole reason the number exists.
   */
  function applyPositions(resequence: (fields: OrderedField[]) => OrderedField[]) {
    setRows((current) => {
      const positions = new Map(
        resequence(orderedFieldsFrom(current, profile)).map((row) => [row.key, row.sortOrder]),
      );
      return current.map((row) => {
        const next = positions.get(rowKey(row));
        return next === undefined || next === row.sortOrder ? row : { ...row, sortOrder: next };
      });
    });
  }

  function moveField(key: string, direction: "up" | "down") {
    applyPositions((fields) => moveRow(fields, key, direction));
  }

  /**
   * Number both blocks 1..n over the order the file would publish them in.
   *
   * What it is *for* is the review that has declared nothing: pressing it turns
   * the header a reviewer is already looking at into positions they can then
   * edit, instead of asking them to type a number against six hundred rows to
   * move the third one. It is also the way out of a collision, which is what
   * the notice points at.
   */
  function renumberAll() {
    applyPositions(renumber);
    setNotice(
      "Every statistic and every excluded field now carries the position its column " +
        "already has in the published file — so this changed no header. The numbers " +
        "will not read 1, 2, 3 down this table, because the table is sorted by field " +
        "name and the file is not. Edit them to move a column, then Save.",
    );
  }

  /**
   * Every field with every Binding that reads it, and how much per-report work
   * the review holds.
   *
   * Over `rows` rather than the filtered set, so the pills count the review
   * rather than counting whatever the last pill left on screen.
   */
  const allGroups = useMemo(() => groupByField(rows), [rows]);
  const triage = useMemo(() => triageCounts(allGroups), [allGroups]);

  /**
   * The fields on screen.
   *
   * **Every queue filters whole groups, never the rows inside one.** Grouping a
   * filtered set of rows was the first shape of this and it quietly lied: a
   * field whose coverage Binding is classified and whose passing one is not
   * would reach `childRows` holding half its siblings, so the report that does
   * own a statistic rendered as *inherited* — the screen reporting a decision
   * nobody had taken back. Filtering groups keeps every field whole, and the
   * Needs-review queue becomes "fields with something still to classify",
   * which is the question the reviewer is actually asking.
   *
   * The two per-report queues read the **same predicates the pills counted
   * with**, so a pill saying 7 over a list of 22 is not reachable.
   */
  const visibleGroups = useMemo(() => {
    if (queue === "unreviewed") {
      return allGroups.filter((group) =>
        group.rows.some((row) => row.role === "ignored" && !row.reviewed),
      );
    }
    if (queue === "duplicated") return allGroups.filter(isDuplicated);
    if (queue === "perReport") return allGroups.filter(isPerReport);
    return allGroups;
  }, [queue, allGroups]);


  /**
   * What giving this field's reports their own statistics would cost.
   *
   * Read before the click rather than after a Run, because two of the answers
   * are fatal rather than cosmetic: the eligibility statistic and a per-90
   * denominator both answer for nothing once scoped (ADR-0056), and on a PFF
   * profile scoping `games_played` drops every player in the division behind a
   * green Save. Nothing warned about this before.
   */
  const hazardFor = useMemo(() => {
    const entries = sportEntries.map((entry) => ({
      key: entry.key,
      per_90_minutes_key: entry.per_90_minutes_key,
    }));
    return (statKey: string | null) =>
      expansionHazard({
        statKey,
        eligibilityStatKey: profile?.eligibility_stat_key ?? null,
        entries,
      });
  }, [sportEntries, profile]);

  /** The reports of a field, named as a reader would name them. */
  const reportRefs = useMemo(
    () => (group: FieldGroup<FieldRow>) =>
      reportsOf(group).map((feedId) => ({ feedId, label: feedName(feedId) })),
    [feedName],
  );

  /**
   * Give every report its own Binding, and suggest each one's own statistic.
   *
   * The suggestion is the step that makes this proportional to the work rather
   * than to the page: the September import already created
   * `pass_interceptions`, `cov_interceptions` and the rest, so a reviewer
   * confirms four matches instead of typing them. Where nothing matches the
   * child keeps the inherited statistic and waits for a human, which is the
   * right answer for a report whose name the dictionary does not carry.
   *
   * No banner. The split writes a row per report and badges each one, directly
   * below the control that was just pressed, so a sentence saying the field now
   * reads each report on its own restated what it sat on top of.
   */
  function giveEachReportItsOwn(group: FieldGroup<FieldRow>) {
    const refs = reportRefs(group);
    const reports = refs.map((ref) => ref.feedId);
    if (reports.length === 0) return;
    const matched = matchReportsFor(group, refs, sportEntries);
    setRows((current) => {
      const materialised = materialiseReports(current, group.path, reports);
      return materialised.map((row) =>
        row.path === group.path && row.feedId && matched[row.feedId]
          ? { ...row, statKey: matched[row.feedId]! }
          : row,
      );
    });
    setExpanded((current) => new Set(current).add(group.path));
  }

  // The reading is waited for as well as the profile, because the rows are
  // built from it once (see the seeding effect) and a table rendered before it
  // lands would say "Nothing here. Re-read the source" on a profile that has
  // six hundred fields and needs no re-read at all.
  //
  // **Its failure is not an error here**, unlike the three queries behind
  // `error`. Those decide what the screen *is*; this one decides only whether
  // the per-report disclosure can be offered, so a field review stays workable
  // without it rather than turning into a red banner. `isPending` is false once
  // the query has errored, so this falls through to the table.
  if (!profile || reportsQuery.isPending) {
    return (
      <Layout breadcrumbs={[{ label: "Sources", to: "/sources" }]}>
        {error ? <Banner tone="danger" title={error} /> : <Spinner label="Loading fields" />}
      </Layout>
    );
  }

  return (
    <Layout
      breadcrumbs={[
        { label: "Sources", to: "/sources" },
        { label: profile.name, to: `/sources/${profile.id}` },
        { label: "Fields" },
      ]}
      title="Fields"
      description="What each path in the source means, and which Feed it came from. A field left carried is not a problem: it reaches the raw file untouched and never the ranking."
      wide
      actions={
        <>
          {/* Each of the three says what *it* is doing, not merely that
              something is. `busy` greys all three out together, which tells a
              reader the screen is working and not which of the three they
              pressed — and on a thirteen-report source the re-read is two
              minutes of looking at a dimmed button. */}
          <Button icon={<RefreshIcon size={16} />} onClick={() => reRead.mutate()} disabled={busy}>
            {reRead.isPending
              ? reReadElapsedMs >= 5000
                ? `Re-reading the source… ${Math.round(reReadElapsedMs / 1000)}s`
                : "Re-reading the source…"
              : "Re-read the source"}
          </Button>
          <Button variant="primary" onClick={() => attemptSave()} disabled={busy}>
            {save.isPending ? "Saving…" : "Save fields"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-lg">
        {error && <Banner tone="danger" title={error} />}
        {notice && <Banner tone="success" title={notice} />}

        {/* What is in the way of a Save, counted rather than described: each
            one is written under the row it belongs to, and a banner repeating
            the first of them is how this screen used to report all of them. */}
        {marked && (
          <Banner tone="danger" title={marked.title}>
            {marked.detail}
          </Banner>
        )}

        {/* A statement about the Weight column as a whole, so it sits above the
            table rather than in one of its cells. Never blocks a Save.

            **One state only**: every scored row weighted, and the percentages
            not adding to 100. The warning about a review mixing percentages
            with blanks was removed — it described something true, but the
            state is a normal one here, so it fired on ordinary work. With the
            per-row share line gone from the Weight column, this banner is now
            the only place the screen says anything about the set of weights as
            a whole. */}
        {weightNoticeForReview && (
          <Banner tone={weightNoticeForReview.tone} title={weightNoticeForReview.title}>
            {weightNoticeForReview.message}
          </Banner>
        )}

        {/* Only ever one thing: two different fields claiming one position,
            where the tie falls to the alphabetical fallback and so to nobody.
            It says nothing about a review that numbers some fields and not
            others, which is the normal and intended state — the mistake the
            Weight column's removed warnings already made. */}
        {orderNoticeForReview && (
          <Banner tone="warning" title="Two fields claim one position">
            {orderNoticeForReview}
          </Banner>
        )}

        <Card
          title={
            outstanding > 0
              ? `${outstanding} field${outstanding === 1 ? "" : "s"} still to review`
              : "Every field is classified"
          }
          description={
            carriedCount > 0
              ? `${carriedCount} field${carriedCount === 1 ? " is" : "s are"} carried into the raw file untouched. That is a normal state, not a backlog. Only a field set to Ignore is waiting on a decision.`
              : "Marking a field reviewed takes it off this queue without changing what it does."
          }
          actions={
            <div className="flex items-center gap-sm">
              {/* Beside the queue pills rather than in the table, because it
                  acts on the whole review and not on a row — and because it is
                  what a reviewer reaches for *before* typing any position: it
                  turns the header they are already looking at into numbers
                  they can edit. */}
              <Button
                variant="secondary"
                onClick={renumberAll}
                title="Write 1, 2, 3… onto the statistics and the excluded fields, in the order the file publishes them now."
              >
                Renumber
              </Button>
              <PillGroup
              value={queue}
              onChange={(value) => setQueue(value)}
              options={[
                { value: "all", label: `All ${rows.length}` },
                { value: "unreviewed", label: `Needs review ${outstanding}` },
                // The per-report work and how much of it is done. Both are
                // gated on there being any: a single-Feed profile — most of
                // this portal — has no duplicated field, and two pills reading
                // zero would describe a job it does not have. On a profile
                // that does, `Per report 0` is worth showing, because zero
                // there means "none of the 67 has been worked yet" rather than
                // an empty queue.
                ...(triage.duplicated > 0
                  ? ([
                      { value: "duplicated", label: `Duplicated ${triage.duplicated}` },
                      { value: "perReport", label: `Per report ${triage.perReport}` },
                    ] as const)
                  : []),
              ]}
              />
            </div>
          }
          flush
        >
          {visibleGroups.length === 0 ? (
            <div className="p-lg">
              <Muted>
                {queue === "perReport"
                  ? "No field has been given a statistic per Feed yet. Open a duplicated field to see what each Feed publishes for it."
                  : queue === "duplicated"
                    ? "No field is published by more than one Feed. Re-read the source if it has not been read since these Bindings were saved."
                    : "Nothing here. Re-read the source to pick up fields added since it was saved."}
              </Muted>
            </div>
          ) : (
            <TableScroll stickyHeader>
              {/* The heading stays on screen: this table is 472 rows on the
                  PrestoSports profile, and a reviewer a hundred rows down is
                  reading seven unlabelled columns.

                  `table-dense` is what makes that possible rather than a
                  preference — the heading can only stick in a box with no
                  horizontal scroller, and at the standard padding this table
                  overflows its box by four pixels. The stylesheet has the
                  measurements. */}
              <table className="table table-dense">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Feed</th>
                    <th>Sample</th>
                    {/* Guaranteed room for "Exclude from ranks", the longest Role.
                                Without it the Statistic column's `w-full` takes the
                                slack from here instead and leaves this a bare
                                chevron — the one control whose value says whether a
                                field is ranked at all. */}
                            <th className="min-w-[9.5rem]">Role</th>
                            {/* **The slack lands on Field, not on Statistic.** It used to go here, on
                                the argument that every other column holds a badge, a short select or a
                                number while this one holds a name — true while the table was capped at
                                1200px and every column was starved. Full width it is the wrong column:
                                measured at 1920px, Statistic took 673px to hold a 384px control, and on
                                a block where every row is *Ignore* it renders nothing at all and
                                reserved all of it anyway. A path is the longest and most variable thing
                                in the row — ESPN soccer publishes
                                `splits.categories.defensive.inneffectiveTackles` — so Field is where an
                                extra pixel buys something. */}
                            <th className="min-w-[14rem]">Statistic</th>
                    <th>Weight %</th>
                    {/* After Weight and before Reviewed: both are per-field
                        numbers somebody types, and Reviewed is the answer given
                        once the rest of the row is settled. */}
                    {/* *Priority* rather than *Order*, because the number is a
                        sorting value and not a seat: ten fields at 1 is a tie,
                        not ten first columns. The hint says the rest. */}
                    <th>
                      <HintedHeading label="Order priority" hint={ORDER_HINT} />
                    </th>
                    <th>Reviewed</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleGroups.map((group) => (
                    <FieldGroupRows
                      key={group.path}
                      group={group}
                      feedName={feedName}
                      feedsRead={feedsRead}
                      profile={profile}
                      stats={stats}
                      sportEntries={sportEntries}
                      groupLabels={groupLabels}
                      outOfSportFor={outOfSportFor}
                      hazard={hazardFor}
                      problemByRow={problemByRow}
                      rowAnchors={rowAnchors}
                      newStatFor={newStatFor}
                      setNewStatFor={setNewStatFor}
                      setRows={setRows}
                      open={expanded.has(group.path)}
                      onToggle={() =>
                        setExpanded((current) => {
                          const next = new Set(current);
                          if (next.has(group.path)) next.delete(group.path);
                          else next.add(group.path);
                          return next;
                        })
                      }
                      nudgeDisabled={nudgeDisabled}
                      onMove={moveField}
                      onGiveEachReportItsOwn={() => giveEachReportItsOwn(group)}
                      onCollapse={() => {
                        setRows((current) => collapseField(current, group.path));
                        setExpanded((current) => {
                          const next = new Set(current);
                          next.delete(group.path);
                          return next;
                        });
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </Card>

        <Card
          title="Preview"
          description="A sample of players as the Bindings above read them, including changes not yet saved. Worth a look before a Run rather than after one."
          actions={
            <Button size="sm" icon={<EyeIcon size={14} />} onClick={() => runPreview.mutate()} disabled={busy}>
              {runPreview.isPending ? "Previewing…" : "Preview parsed rows"}
            </Button>
          }
          flush={Boolean(preview) && !runPreview.isPending}
        >
          {/* A spinner rather than the last preview held under a dimmed button.
              Replacing the table is deliberate: a stale table beside a working
              spinner is the one thing worse than no feedback, because the rows
              on screen are the previous Bindings' answer and nothing says so.
              `flush` is dropped while it runs, so the spinner gets the Card's
              own padding instead of sitting against a flush edge. */}
          {runPreview.isPending ? (
            <Spinner
              label={preview ? "Reading the sample again" : "Reading a sample of players"}
            />
          ) : preview ? (
            <>
              <div className="border-b border-hairline-soft bg-surface-soft px-lg py-sm">
                <div className="flex flex-wrap items-center gap-xs">
                  <Badge tone="accent">{preview.records_seen.toLocaleString()} records read</Badge>
                  <Badge tone="accent">{preview.stat_keys.length} statistics bound</Badge>
                  <Badge tone="accent">{preview.carried_columns.length} carried columns</Badge>
                </div>
              </div>

              {preview.rows.length === 0 ? (
                <div className="p-lg">
                  <Banner tone="warning" title="No players came back">
                    Usually the Identity Key names a field this source does not publish, so every record is
                    skipped.
                  </Banner>
                </div>
              ) : (
                <TableScroll stickyHeader>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Team</th>
                        <th>Position</th>
                        {/* Only where one was bound, exactly as the published
                            header carries it — a permanently empty column
                            would claim every source reports a draft season. */}
                        {preview.rows.some((row) => row.draft_season !== null) && (
                          <th>Draft season</th>
                        )}
                        <th>Group</th>
                        {preview.stat_keys.map((key) => (
                          <th key={key} className="text-right">
                            {key}
                          </th>
                        ))}
                        {/* The carried columns are the raw file. With carried the
                            default for an unrecognised number, a preview of the
                            statistics alone would show a handful of grades and no
                            sign of the columns about to be published. */}
                        {preview.carried_columns.map((key) => (
                          <th key={`carried:${key}`} className="text-right">
                            {key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((row) => (
                        <tr key={row.identity}>
                          <td className="text-body-sm-medium text-ink">
                            {row.name ?? <Muted>—</Muted>}
                          </td>
                          <td>{row.team ?? <Muted>—</Muted>}</td>
                          <td>{row.position ?? <Muted>—</Muted>}</td>
                          {preview.rows.some((entry) => entry.draft_season !== null) && (
                            <td>{row.draft_season ?? <Muted>—</Muted>}</td>
                          )}
                          <td>
                            <Badge>{row.canonical_position_group}</Badge>
                          </td>
                          {preview.stat_keys.map((key) => (
                            <td key={key} className="text-right">
                              {row.stats[key] ?? <Muted>—</Muted>}
                            </td>
                          ))}
                          {preview.carried_columns.map((key) => (
                            <td key={`carried:${key}`} className="text-right">
                              {row.carried[key] === undefined || row.carried[key] === null ? (
                                <Muted>—</Muted>
                              ) : (
                                String(row.carried[key])
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroll>
              )}
            </>
          ) : (
            <Muted tone="text-info-ink">
              Nothing previewed yet. Press Preview parsed rows to see how the Bindings read a
              sample of players.
            </Muted>
          )}
        </Card>
      </div>
    </Layout>
  );
}

/**
 * One field, and the reports that publish it.
 *
 * The screen used to be one row per `(report, path)` pair with a **Split**
 * button: pressing it on `penalties` produced thirteen sibling rows wedged
 * into a six-hundred-row table, each needing its statistic picked again. On
 * the FBS profile that is 198 statistic choices across the 67 duplicated
 * fields, to express about ten real decisions.
 *
 * So a field is one row that **discloses** its reports instead. Collapsed, the
 * page is the 248 fields and does not grow; expanded, the field shows one line
 * per report, each prefilled with the field's own statistic and marked
 * *inherited* rather than chosen, each carrying **that report's own sample**.
 * The reviewer overrides what differs and leaves the rest, so `penalties`
 * stays one decision and `interceptions` becomes two.
 *
 * **Nothing per report is stored until something is overridden.** Inheritance
 * is an affordance of this screen and not a state in the database: a field
 * nobody touched saves as the one unscoped Binding it always was, and the
 * published header does not move because somebody opened a disclosure. The
 * first override materialises *every* report at once, which is forced rather
 * than chosen — an unscoped Binding claims the key on each report, so one
 * unscoped beside one scoped is refused as `colliding_column` (ADR-0056).
 */
function FieldGroupRows({
  group,
  feedName,
  feedsRead,
  profile,
  stats,
  sportEntries,
  groupLabels,
  outOfSportFor,
  hazard,
  problemByRow,
  rowAnchors,
  newStatFor,
  setNewStatFor,
  setRows,
  open,
  onToggle,
  onGiveEachReportItsOwn,
  onCollapse,
  nudgeDisabled,
  onMove,
}: {
  group: FieldGroup<FieldRow>;
  feedName: (id: string) => string;
  /** Whether **every** one of this profile's Feeds has been read, which is what
   *  licenses saying a path is published by none — see `reportsKnown`. */
  feedsRead: boolean;
  profile: SourceProfile;
  stats: StatEntry[];
  sportEntries: StatEntry[];
  /** What feeds each of the sport's groups, for the Statistic menu's detail
   *  panel. Threaded like `sportEntries` rather than queried per row. */
  groupLabels: Readonly<Record<string, readonly string[]>>;
  outOfSportFor: (statKey: string | null) => StatEntry | null;
  hazard: (statKey: string | null) => ExpansionHazard | null;
  problemByRow: Map<string, string>;
  rowAnchors: React.MutableRefObject<Map<string, HTMLElement>>;
  newStatFor: string | null;
  setNewStatFor: React.Dispatch<React.SetStateAction<string | null>>;
  setRows: React.Dispatch<React.SetStateAction<FieldRow[]>>;
  open: boolean;
  onToggle: () => void;
  onGiveEachReportItsOwn: () => void;
  onCollapse: () => void;
  /** Whether this field is already at an end of its block, per direction. */
  nudgeDisabled: (key: string) => { up: boolean; down: boolean };
  onMove: (key: string, direction: "up" | "down") => void;
}) {
  const reports = reportsOf(group);
  const duplicated = reports.length > 1;

  /**
   * The Binding the field's own row edits.
   *
   * The unscoped one where there is one. A field with exactly one row and no
   * unscoped Binding is a single-report field already scoped — most of this
   * portal — and its row edits that. A field materialised across several
   * reports has no single Binding to edit, so its cells say so and the editing
   * happens in the children.
   */
  const primary =
    group.rows.find((row) => row.feedId === null) ??
    (group.rows.length === 1 ? group.rows[0]! : null);

  // A materialised field opens on its own: its whole configuration lives in
  // the children, and a closed row would hide the only place it can be read.
  const perReport = primary === null;
  const showReports = duplicated && (open || perReport);

  // Only a value can be read per report. A player's name, team, position and
  // Identity Key are facts about the player rather than about the report, and
  // the parser reads them from whichever Feed carries them whatever a scope
  // says — so offering the control there would offer an edit with no effect.
  const scopable = !primary || primary.role === "carried" || primary.role === "stat";
  const risk = primary ? hazard(primary.statKey) : null;

  /**
   * Give one report its own answer, materialising the field to do it.
   *
   * Both halves in one `setRows`, so a reviewer never sees the intermediate
   * state where thirteen reports have been scoped and none of them edited.
   */
  function overrideReport(feedId: string, patch: Partial<FieldRow>) {
    setRows((current) => {
      const materialised = materialiseReports(current, group.path, reports);
      return materialised.map((row) =>
        row.path === group.path && row.feedId === feedId ? { ...row, ...patch } : row,
      );
    });
  }

  return (
    <>
      <tr
        // Registered so a refusal naming this row can scroll to it, and
        // dropped on unmount — the queue pills unmount rows, and travelling to
        // a stale node would silently do nothing.
        ref={(node) => {
          if (!primary) return;
          if (node) rowAnchors.current.set(rowKey(primary), node);
          else rowAnchors.current.delete(rowKey(primary));
        }}
        // Focusable only while there is something to travel to, and marked for
        // the stylesheet. Not `aria-invalid`, which says nothing on a row: the
        // reason below it is the `role=alert` that gets announced.
        tabIndex={primary && problemByRow.has(rowKey(primary)) ? -1 : undefined}
        data-problem={primary && problemByRow.has(rowKey(primary)) ? "" : undefined}
      >
        {/* `whitespace-nowrap` is load-bearing, not cosmetic. `.code` carries
            `break-all`, and adding the disclosure control to this cell gave the
            auto-layout table one more thing to fit — so the column collapsed
            and every path rendered one character per line, a six-hundred-row
            table of vertical text. Found by opening the screen; no test sees
            column widths. */}
        <td>
          <div className="flex items-center gap-xs whitespace-nowrap">
            {duplicated && !perReport ? (
              <button
                type="button"
                className="shrink-0 rounded px-2xs text-ink-subtle hover:text-ink"
                aria-expanded={open}
                aria-label={`${open ? "Hide" : "Show"} the ${reports.length} Feeds publishing ${group.path}`}
                onClick={onToggle}
              >
                {open ? "▾" : "▸"}
              </button>
            ) : (
              <span className="inline-block w-[1.25rem] shrink-0" aria-hidden="true" />
            )}
            {/* `title`, and deliberately not the portal's `Tooltip`. That one
                was tried here and reverted: its bubble opens *below* the
                trigger, which in a dense table lands on top of the next row
                and hides the path underneath the one being read. A browser's
                own tooltip is placed by the browser, never covers what it
                describes, and costs no DOM in 257 rows. The delay before it
                appears is the price, and it is the smaller problem. */}
            <Code className="max-w-[22rem] truncate" title={group.path}>
              {group.path}
            </Code>
            {group.rows.some((row) => row.isNew) && <Badge tone="success">new</Badge>}
          </div>
        </td>
        <td>
          <ReportsCell
            group={group}
            reports={reports}
            feedName={feedName}
            feedCount={profile.feeds.length}
            feedsRead={feedsRead}
            perReport={perReport}
            scopable={scopable}
            risk={risk}
            onGiveEachReportItsOwn={onGiveEachReportItsOwn}
            onCollapse={onCollapse}
          />
        </td>
        <td>
          <SampleCell
            value={
              perReport
                ? "—"
                : primary && primary.samples.length > 0
                  ? String(primary.samples[0])
                  : "—"
            }
          />
        </td>
        {primary ? (
          <>
            <td>
              <SelectCompact
                value={primary.role}
                onChange={(e) =>
                  update(setRows, rowKey(primary), { role: e.target.value as Role })
                }
              >
                {ROLE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectCompact>
            </td>
            <td>
              {primary.role === "stat" && (
                <StatisticCell
                  groupLabels={groupLabels}
                  value={primary.statKey}
                  weight={primary.weight}
                  entries={sportEntries}
                  outOfSport={outOfSportFor(primary.statKey)}
                  label={`Statistic for ${primary.path}`}
                  newOpen={newStatFor === rowKey(primary)}
                  onChange={(statKey, weight) =>
                    update(setRows, rowKey(primary), { statKey, weight })
                  }
                  onNew={() =>
                    setNewStatFor((current) =>
                      current === rowKey(primary) ? null : rowKey(primary),
                    )
                  }
                />
              )}
            </td>
            {/* Blank rather than 1 where nothing is declared, and the two
                rank identically: filling it in would show 69 decisions where
                somebody made 27. */}
            <td>
              {primary.role === "stat" && (
                <WeightCell
                  path={primary.path}
                  weight={primary.weight}
                  onChange={(weight) => update(setRows, rowKey(primary), { weight })}
                />
              )}
            </td>
            {/* Offered on every role, unlike Weight: a field moved between
                Statistic and *Exclude from ranks* is moving between the two
                blocks that publish columns, and a cell that disappeared on the
                way would lose the placement in the gap. The cell disables
                itself for the roles that publish no column of their own. */}
            <td>
              <div className="flex items-center gap-2xs">
                <OrderCell
                  path={primary.path}
                  sortOrder={primary.sortOrder}
                  role={primary.role}
                  onChange={(sortOrder) => update(setRows, rowKey(primary), { sortOrder })}
                />
                <OrderNudge
                  path={primary.path}
                  disabled={nudgeDisabled(rowKey(primary))}
                  onMove={(direction) => onMove(rowKey(primary), direction)}
                />
              </div>
            </td>
            <td>
              <Checkbox
                checked={primary.reviewed || primary.role !== "ignored"}
                disabled={primary.role !== "ignored"}
                onChange={(e) => update(setRows, rowKey(primary), { reviewed: e.target.checked })}
              />
            </td>
          </>
        ) : (
          // Materialised. The cells are deliberately empty rather than showing
          // the first report's answer, which would read as the field's own and
          // be edited as if it were.
          <td colSpan={5}>
            <Muted>Each Feed below is read on its own.</Muted>
          </td>
        )}
      </tr>

      {primary && problemByRow.has(rowKey(primary)) && (
        <ProblemNote colSpan={8} message={problemByRow.get(rowKey(primary))!} />
      )}

      {primary && primary.role === "stat" && newStatFor === rowKey(primary) && (
        <NewStatisticRow colSpan={8}>
          <NewStatisticForm
            sport={profile.sport}
            entries={stats}
            onCancel={() => setNewStatFor(null)}
            onCreated={(statKey, weight) => {
              // Onto the row it was created from, leaving the rest of the
              // review — including a `yards` split across thirteen Feeds —
              // untouched.
              //
              // **A blank Weight leaves the row's own alone rather than
              // clearing it.** Setting it unconditionally lost a weight
              // somebody had already typed into this row's cell the moment
              // they used "+ New" to name the statistic, with nothing on
              // screen saying so. Blank here means "nothing declared in this
              // form", not "no weight on this row".
              update(setRows, rowKey(primary), {
                statKey,
                ...(weight == null ? {} : { weight }),
              });
              setNewStatFor(null);
            }}
          />
        </NewStatisticRow>
      )}

      {showReports &&
        childRows(group).map((child) => {
          const key = rowKey({ feedId: child.feedId, path: group.path });
          // The row this child already has, where it has one. Only a stored row
          // can carry a Reviewed state, and only it can be told apart from an
          // inherited line by anything other than the badge.
          const own = group.rows.find((row) => rowKey(row) === key) ?? null;
          return (
            <Fragment key={key}>
              <tr
                data-child=""
                ref={(node) => {
                  if (node) rowAnchors.current.set(key, node);
                  else rowAnchors.current.delete(key);
                }}
                tabIndex={problemByRow.has(key) ? -1 : undefined}
                data-problem={problemByRow.has(key) ? "" : undefined}
              >
                <td>
                  <div className="flex items-center gap-xs whitespace-nowrap pl-lg">
                    <span className="text-ink-subtle" aria-hidden="true">
                      └
                    </span>
                    <span className="text-body-sm text-ink">{feedName(child.feedId)}</span>
                  </div>
                </td>
                <td>
                  {child.inherited ? (
                    <Badge>inherited</Badge>
                  ) : (
                    <Badge tone="accent">own statistic</Badge>
                  )}
                </td>
                {/* This report's own value, which is the evidence the decision
                    rests on: 3,951 in the passing report against 143 in the
                    rushing one is what says they are two statistics. */}
                <td>
                  <SampleCell value={child.sample === undefined ? "—" : String(child.sample)} />
                </td>
                <td>
                  <SelectCompact
                    value={child.role}
                    disabled={Boolean(risk)}
                    onChange={(e) => overrideReport(child.feedId, { role: e.target.value as Role })}
                  >
                    {ROLE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </SelectCompact>
                </td>
                <td>
                  {child.role === "stat" &&
                    (risk ? (
                      <Muted>{child.statKey ?? "—"}</Muted>
                    ) : (
                      /* The Direction is in the menu's own label — `statOptions`
                         appends "(lower is better)", which is how every other
                         row on both screens has always shown it. A badge beside
                         it said the same thing again in 92px and squeezed the
                         menu to a bare chevron, so the value a reviewer is
                         choosing could not be read at all. */
                      <StatisticCell
                        groupLabels={groupLabels}
                        value={child.statKey}
                        weight={child.weight}
                        entries={sportEntries}
                        outOfSport={outOfSportFor(child.statKey)}
                        label={`Statistic for ${group.path} in ${feedName(child.feedId)}`}
                        newOpen={newStatFor === key}
                        onChange={(statKey, weight) =>
                          overrideReport(child.feedId, { statKey, weight })
                        }
                        onNew={() => setNewStatFor((current) => (current === key ? null : key))}
                      />
                    ))}
                </td>
                <td>
                  {child.role === "stat" && !risk && (
                    <WeightCell
                      path={`${group.path} (${feedName(child.feedId)})`}
                      weight={child.weight}
                      onChange={(weight) => overrideReport(child.feedId, { weight })}
                    />
                  )}
                </td>
                {/* A materialised report publishes its own column, so it has
                    its own position. Editing it materialises the field exactly
                    as editing the Role or the Weight does — `overrideReport`
                    holds that rule — and the value shown until then is the
                    field's, because that is the position its column has. */}
                <td>
                  {!risk && (
                    <OrderCell
                      path={`${group.path} (${feedName(child.feedId)})`}
                      sortOrder={child.sortOrder}
                      role={child.role}
                      onChange={(sortOrder) => overrideReport(child.feedId, { sortOrder })}
                    />
                  )}
                </td>
                {/* Only a report with a Binding of its own. An inherited line
                    has no row to mark and the field answers for it above, so a
                    checkbox there would ask one question thirteen times.

                    **A materialised field has no row above**, which is how this
                    went missing: every one of its reports is a stored Binding,
                    and an `ignored` one with no checkbox sits in the
                    Needs-review count for ever with no way to clear it. */}
                <td>
                  {own && (
                    <Checkbox
                      checked={own.reviewed || own.role !== "ignored"}
                      disabled={own.role !== "ignored"}
                      onChange={(e) => update(setRows, key, { reviewed: e.target.checked })}
                    />
                  )}
                </td>
              </tr>

              {problemByRow.has(key) && (
                <ProblemNote colSpan={8} message={problemByRow.get(key)!} />
              )}

              {child.role === "stat" && newStatFor === key && (
                <NewStatisticRow colSpan={8}>
                  <NewStatisticForm
                    sport={profile.sport}
                    entries={stats}
                    onCancel={() => setNewStatFor(null)}
                    onCreated={(statKey, weight) => {
                      // Blank leaves this report's own weight alone; see the
                      // field row's form above.
                      overrideReport(child.feedId, {
                        statKey,
                        ...(weight == null ? {} : { weight }),
                      });
                      setNewStatFor(null);
                    }}
                  />
                </NewStatisticRow>
              )}

              {/* Said once, under the last report, rather than against every
                  one of thirteen. Keyed on the last report *rendered* and not
                  on the last one still inheriting: a field with an override
                  already on its final report would otherwise lose the warning
                  that the remaining reports have not yet paid for. Skipped
                  where the field is already read apart and the cost is spent. */}
              {/* Only ever a reason the field must not be split — see
                  `ExpansionHazard`. Said once, under the last Feed, rather
                  than against each of thirteen. */}
              {risk && !perReport && child.feedId === reports[reports.length - 1] && (
                <ProblemNote colSpan={8} message={risk.message} />
              )}
            </Fragment>
          );
        })}
    </>
  );
}

/**
 * How many Feeds publish this field, and the controls that change that.
 *
 * Three states, and the third is the one the old screen had no answer for:
 *
 * - **one Feed** — the ordinary case, and most of this portal. The Feed's
 *   name, or `All Feeds` where nothing has been read.
 * - **several Feeds, one Binding** — the field fans out into a column each
 *   (ADR-0055) and may instead be given a statistic per Feed.
 * - **several Feeds, a Binding each** — already decided, and undoable.
 *
 * A profile opened cold is a fourth state masquerading as the first.
 * `seenIn` is filled by a discovery run only, so a field read back from a
 * saved Binding knows nothing about which Feeds publish its path, and the
 * honest thing is to say so rather than to offer a disclosure onto an empty
 * list — the same friction Split has always had.
 *
 * **Feed rather than report, deliberately.** `CONTEXT.md` makes Feed the term
 * and keeps *report* for the published header `<Statistic> - <Report>`, which
 * is PFF's word for its own Feeds. A control reading "13 reports" would be
 * wrong on Wyscout's Stat Filters and on ESPN's one-Feed-per-club profiles,
 * which this screen also serves.
 */
function ReportsCell({
  group,
  reports,
  feedName,
  feedCount,
  feedsRead,
  perReport,
  scopable,
  risk,
  onGiveEachReportItsOwn,
  onCollapse,
}: {
  group: FieldGroup<FieldRow>;
  reports: string[];
  feedName: (id: string) => string;
  feedCount: number;
  feedsRead: boolean;
  perReport: boolean;
  scopable: boolean;
  risk: ExpansionHazard | null;
  onGiveEachReportItsOwn: () => void;
  onCollapse: () => void;
}) {
  if (perReport || group.scoped) {
    return (
      <div className="flex items-center gap-xs">
        <Badge tone="accent">
          {/* The accent tone carries "read apart"; the button beside it
              already says so in words. Spelling it out a third time cost
              85px of a column that was overflowing. */}
          {reports.length > 1 ? `${reports.length} Feeds` : feedName(reports[0]!)}
        </Badge>
        <Button
          size="sm"
          variant="ghost"
          title="Read every report under one Binding again. The first report's statistic stands for all of them and the others are discarded; a field more than one report publishes still reaches the file once per report."
          onClick={onCollapse}
        >
          Merge
        </Button>
      </div>
    );
  }

  if (reports.length > 1) {
    return (
      <div className="flex items-center gap-xs">
        <Badge tone="warning">{reports.length} Feeds</Badge>
        {scopable && (
          <Button
            size="sm"
            variant="ghost"
            disabled={Boolean(risk)}
            title={
              risk
                ? risk.message
                : `${reports.length} reports publish this path, and it reaches the file once per report either way. Give each one its own statistic where they mean different things — which is how one report's ‘interceptions thrown’ and another's ‘caught’ get opposite Directions.`
            }
            onClick={onGiveEachReportItsOwn}
          >
            Per Feed
          </Button>
        )}
      </div>
    );
  }

  // No reports on a source with more than one Feed, which is two different
  // things: nobody has looked, or every Feed has been read and none publishes
  // this path. Saying "All Feeds" would state as fact the thing the reviewer is
  // deciding, and asking for a re-read in the second case is an instruction
  // that cannot help — so `unreadNotice` tells them apart on `read_at`.
  const unread = unreadNotice(reports.length, feedCount, feedsRead);
  if (unread) {
    return (
      <span
        title={
          feedsRead
            ? "Every Feed has been read and none of them published this path. The Binding is on a field the publisher no longer sends, or never did."
            : "A saved Binding records which Feed it reads, never which Feeds publish its path. Re-read the source to find out."
        }
      >
        <Badge>{unread}</Badge>
      </span>
    );
  }

  return <Badge>{reports.length === 1 ? feedName(reports[0]!) : "All Feeds"}</Badge>;
}

/**
 * One saved Binding as an editable row, with what is known about its reports.
 *
 * `seenIn` and `samplesByFeed` used to be empty here, with a comment saying a
 * stored Binding records which report it reads and never which reports publish
 * its path. That is still true of the Binding — and it is why the per-report
 * disclosure appeared only after a re-read and was gone again on the next page
 * load (`field-mapping-usability/03`). The fact now has somewhere to live:
 * ADR-0061 keeps each Feed's last reading on the Feed, so the answer arrives
 * with the page instead of with a three-minute fetch.
 *
 * Still empty for a profile whose Feeds nothing has ever read, which is the
 * honest residue: no inference can supply a list nobody has measured, and
 * assuming every Feed publishes every path would file a Binding per report for
 * reports publishing nothing — ADR-0055's fan-out then publishes an empty
 * column per player for each one.
 */
function fromBinding(binding: Binding, evidence: Map<string, FieldEvidence>): FieldRow {
  return {
    path: binding.path,
    role: binding.role,
    statKey: binding.stat_key,
    reviewed: binding.reviewed,
    isNew: false,
    samples: [],
    occurrences: null,
    feedId: binding.feed_id,
    weight: binding.weight,
    sortOrder: binding.sort_order,
    parseRule: binding.parse_rule,
    ...evidenceFor(evidence, binding.path),
  };
}

function toDraft(row: FieldRow): BindingDraft {
  return {
    path: row.path,
    role: row.role,
    stat_key: row.role === "stat" ? row.statKey : null,
    parse_rule: row.parseRule,
    reviewed: row.reviewed || row.role !== "ignored",
    feed_id: row.feedId,
    // Only a scored field has anything for a weight to be relative to, which is
    // the rule the server applies to `stat_key` on the same line.
    weight: row.role === "stat" ? row.weight : null,
    // **Not narrowed by role, unlike the two above.** A position is not a
    // scoring decision, and `stat` and `carried` are exactly the two roles a
    // reviewer moves a field between while meaning it to stay where it is in
    // the header — the server keeps it for the same reason.
    sort_order: row.sortOrder,
  };
}

function newRow(
  field: DiscoveredField,
  feedId: string | null,
  seenIn: string[],
  samplesByFeed: Record<string, unknown[]>,
): FieldRow {
  return {
    path: field.path,
    role: field.suggested_role,
    statKey: field.suggested_stat_key,
    reviewed: false,
    isNew: true,
    samples: field.samples,
    occurrences: field.occurrences,
    feedId,
    // The Stat Dictionary's default weight for the statistic this field resolved
    // to, on the same terms as the role and statistic above.
    //
    // **This comment used to say discovery had no weight to suggest.** That was
    // true while the weight lived only on the Binding: a weight is a judgement
    // about one ranking, so there was no per-statistic answer to inherit. The
    // entry now carries a `default_weight` — a default for Bindings that do not
    // exist yet, not a weight the ranker reads — so there is, and starting a
    // review from blank would throw away the one somebody filed.
    //
    // Null where the entry declares none, which is most of them, and the row's
    // Weight box stays blank rather than showing a 1 nobody chose.
    weight: field.suggested_weight ?? null,
    // **Undeclared, and discovery has nothing to suggest.** Where a column
    // belongs in the header is a judgement about one file, so there is no
    // per-statistic answer to inherit the way `suggested_weight` is inherited
    // from the dictionary entry. It is also what makes a re-read safe: a field
    // the publisher has just added lands at the end of its block rather than in
    // the middle of a sequence somebody has already arranged.
    sortOrder: null,
    parseRule: {},
    seenIn,
    samplesByFeed,
  };
}

/**
 * Keep what is already classified; add only what the source has gained.
 *
 * Discovery now returns one entry per Feed *and* path, so a path several
 * reports publish arrives several times. What that becomes on screen depends on
 * what is already there: an existing Binding's scope is never changed by a
 * re-read — proposing one over an unscoped Binding would rename a published
 * column on a read nobody meant as a change — while a path nobody has
 * classified takes the scope the server suggests.
 */
function merge(current: FieldRow[], discovered: DiscoveredField[]): FieldRow[] {
  const seenIn = new Map<string, string[]>();
  const byPath = new Map<string, DiscoveredField[]>();
  // Each report's own samples, which the per-report expansion decides from.
  // Built here rather than on the row because discovery lists a duplicated path
  // once per Feed and every one of those entries has to reach every row that
  // reads the path — including the unscoped row, which is the one showing the
  // disclosure.
  const samplesByPath = new Map<string, Record<string, unknown[]>>();
  for (const field of discovered) {
    const fields = byPath.get(field.path) ?? [];
    fields.push(field);
    byPath.set(field.path, fields);
    if (!field.feed_id) continue;
    const feeds = seenIn.get(field.path) ?? [];
    if (!feeds.includes(field.feed_id)) feeds.push(field.feed_id);
    seenIn.set(field.path, feeds);
    const samples = samplesByPath.get(field.path) ?? {};
    samples[field.feed_id] = field.samples;
    samplesByPath.set(field.path, samples);
  }

  const existing = new Map<string, FieldRow[]>();
  for (const row of current) {
    const rows = existing.get(row.path) ?? [];
    rows.push(row);
    existing.set(row.path, rows);
  }

  const merged: FieldRow[] = [];
  for (const [path, fields] of byPath) {
    const first = fields[0];
    if (!first) continue;
    const feeds = seenIn.get(path) ?? [];
    const samples = samplesByPath.get(path) ?? {};
    const rows = existing.get(path);
    existing.delete(path);

    if (!rows || rows.length === 0) {
      if (first.suggested_scoped) {
        for (const field of fields) merged.push(newRow(field, field.feed_id, feeds, samples));
      } else {
        merged.push(newRow(first, null, feeds, samples));
      }
      continue;
    }

    const covered = new Set(rows.map((row) => row.feedId));
    for (const row of rows) {
      const from = fields.find((field) => field.feed_id === row.feedId) ?? first;
      merged.push({
        ...row,
        samples: from.samples,
        occurrences: from.occurrences,
        seenIn: feeds,
        samplesByFeed: samples,
      });
    }
    // A Feed publishing a path that only *other* Feeds' Bindings read is a
    // field nobody has classified, so it gets a row of its own. An unscoped
    // Binding already reads every Feed, so it covers them all.
    if (!covered.has(null)) {
      for (const field of fields) {
        if (field.feed_id && !covered.has(field.feed_id) && !field.already_bound) {
          merged.push(newRow(field, field.feed_id, feeds, samples));
        }
      }
    }
  }

  // Anything the source no longer publishes stays on the list rather than
  // vanishing — a field that disappeared is worth seeing, not hiding.
  //
  // **Its reports are cleared, though, and that matters since ADR-0061.** A
  // re-read reads every Feed, so a path absent from the whole result is a path
  // no Feed publishes — authoritatively. The row was seeded on load from the
  // *stored* reading, which did list reports for it, and carrying those over
  // would have the screen keep offering a disclosure onto reports that have
  // just been measured as no longer publishing the field. Cleared, the Feed
  // cell reads "No Feed publishes this", which is both true and what the
  // reviewer needs to see about a Binding on a dropped column.
  for (const orphans of existing.values()) {
    for (const orphan of orphans) merged.push(withoutReports(orphan));
  }
  return merged;
}



function update(
  setRows: React.Dispatch<React.SetStateAction<FieldRow[]>>,
  key: string,
  patch: Partial<FieldRow>,
): void {
  setRows((current) => current.map((row) => (rowKey(row) === key ? { ...row, ...patch } : row)));
}

/** The human message an API rejection carries, or a fallback for anything else. */
function messageFor(error: unknown, fallback: string): string | null {
  if (!error) return null;
  return error instanceof ApiError ? error.message : fallback;
}
